import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { apiFetch, ApiError, setUnauthorizedHandler } from '../api/client'
import type { CurrentUser, Role } from './types'
import { hasAtLeast } from './types'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'revoked'

export interface AuthContextValue {
  status: AuthStatus
  user: CurrentUser | null
  /** True when the signed-in user holds at least the given role. */
  can: (required: Role) => boolean
  signIn: () => void
  signOut: () => void
  refresh: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/**
 * Sign-in is a full-page navigation to the API, which owns the OIDC flow and
 * hands back a session cookie. The SPA never sees a provider token, so all it
 * can do is ask "who am I?" and react.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<CurrentUser | null>(null)

  const refresh = useCallback(async () => {
    try {
      const me = await apiFetch<CurrentUser>('/api/me')
      setUser(me)
      setStatus('authenticated')
    } catch (error) {
      setUser(null)
      if (error instanceof ApiError && error.problem?.type?.endsWith('access-revoked')) {
        setStatus('revoked')
      } else {
        setStatus('anonymous')
      }
    }
  }, [])

  useEffect(() => {
    // Genuine external-system synchronization: register the API client's 401
    // handler and ask the server who we are.
    setUnauthorizedHandler((reason) => {
      setUser(null)
      setStatus(reason === 'revoked' ? 'revoked' : 'anonymous')
    })
    // `refresh` only sets state after an await, so this cannot cascade renders.
    // oxlint-disable-next-line react/set-state-in-effect
    void refresh()
  }, [refresh])

  const signIn = useCallback(() => {
    // Preserve where the user was headed so the API can return them there.
    const returnUrl = `${window.location.pathname}${window.location.search}`
    window.location.assign(`/signin?returnUrl=${encodeURIComponent(returnUrl)}`)
  }, [])

  const signOut = useCallback(() => {
    window.location.assign('/signout')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      can: (required: Role) => (user ? hasAtLeast(user.role, required) : false),
      signIn,
      signOut,
      refresh,
    }),
    [status, user, signIn, signOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
